import { useEffect, useMemo, useRef, useState } from 'react';
import { compile, FRAG, MAX_FOV, MAX_PITCH, MIN_FOV, VERT } from './Panorama360';
import '../styles/video360.css';

/**
 * A 360 walkthrough VIDEO: the film plays, and the viewer looks around inside it.
 *
 * Same projection as the 360 photos (Panorama360's shader), fed a video frame
 * instead of a still: every displayed frame is one sphere, and the screen shows
 * the slice the viewer is facing. Played as an ordinary <video> the same file
 * is a stretched panorama with bent walls, which is why projection is stored on
 * the row (migration 165) rather than guessed here.
 *
 * WHAT A VIEWER CAN DO, and why each is here:
 *   - drag, arrow keys or tilt the phone to look around (tilt is opt-in: iOS
 *     asks permission, and nobody wants the view moving because they shifted
 *     on the sofa);
 *   - scroll or pinch to zoom;
 *   - jump straight to a room. A minute of film with no chapters makes the
 *     renter who only cares about the kitchen sit through the hallway twice;
 *   - full screen, which on a phone is most of the point.
 *
 * Degrades honestly: no WebGL, or a frame larger than the device can hold as
 * a texture, plays the file flat with a note saying it is a 360 video.
 */

export interface VideoChapter { at: number; section: string; label: string }

const clock = (s: number) => {
  const t = Math.max(0, Math.floor(s));
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`;
};

export function Video360({ src, chapters = [], title, onTime }: {
  src: string; chapters?: VideoChapter[]; title: string;
  /** Reports the playhead, so the editor can mark "this room starts here". */
  onTime?: (seconds: number) => void;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const cam = useRef({ yaw: 0, pitch: 0, fov: 1.3 });
  const redraw = useRef<() => void>(() => {});
  const timeCb = useRef(onTime);
  timeCb.current = onTime;

  const [flat, setFlat] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [length, setLength] = useState(0);
  const [ready, setReady] = useState(false);
  const [hinted, setHinted] = useState(false);
  const [tilt, setTilt] = useState(false);
  // Starts muted: browsers refuse to start a film with sound until a tap, and a
  // walkthrough that blares on a matatu is a walkthrough that gets closed.
  const [muted, setMuted] = useState(true);
  const canTilt = typeof window !== 'undefined' && 'DeviceOrientationEvent' in window
    && window.matchMedia?.('(pointer: coarse)').matches;

  const ordered = useMemo(() => [...chapters].sort((a, b) => a.at - b.at), [chapters]);
  const current = ordered.reduce<VideoChapter | null>((c, ch) => (ch.at <= time + 0.25 ? ch : c), null);

  /* ----- the sphere ------------------------------------------------------ */
  useEffect(() => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video) return;

    const gl = canvas.getContext('webgl', { antialias: true, alpha: false });
    if (!gl) { setFlat(true); return; }

    let program: WebGLProgram;
    try {
      program = gl.createProgram()!;
      gl.attachShader(program, compile(gl, gl.VERTEX_SHADER, VERT));
      gl.attachShader(program, compile(gl, gl.FRAGMENT_SHADER, FRAG));
      gl.linkProgram(program);
      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error('link failed');
    } catch { setFlat(true); return; }
    gl.useProgram(program);

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const aPos = gl.getAttribLocation(program, 'aPos');
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);
    const uYaw = gl.getUniformLocation(program, 'uYaw');
    const uPitch = gl.getUniformLocation(program, 'uPitch');
    const uFov = gl.getUniformLocation(program, 'uFov');
    const uAspect = gl.getUniformLocation(program, 'uAspect');

    const tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE,
      new Uint8Array([24, 28, 34, 255]));
    const maxTex = gl.getParameter(gl.MAX_TEXTURE_SIZE) as number;

    let disposed = false;
    let loop = 0;

    const draw = (upload: boolean) => {
      if (disposed) return;
      if (upload && video.readyState >= 2) {
        gl.bindTexture(gl.TEXTURE_2D, tex);
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 0);
        try { gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, video); }
        catch { setFlat(true); return; }
      }
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.max(1, Math.round(canvas.clientWidth * dpr));
      const h = Math.max(1, Math.round(canvas.clientHeight * dpr));
      if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.uniform1f(uYaw, cam.current.yaw);
      gl.uniform1f(uPitch, cam.current.pitch);
      gl.uniform1f(uFov, cam.current.fov);
      gl.uniform1f(uAspect, canvas.width / canvas.height);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    };
    // Looking around while paused redraws the held frame; it does not re-upload it.
    redraw.current = () => draw(false);

    // A new frame every displayed frame while playing; one upload on a seek.
    const tick = () => { draw(true); loop = video.paused ? 0 : requestAnimationFrame(tick); };
    const onPlay = () => { setPlaying(true); if (!loop) loop = requestAnimationFrame(tick); };
    const onPause = () => { setPlaying(false); draw(true); };
    const onFrame = () => draw(true);
    const onMeta = () => {
      setLength(video.duration || 0);
      if (video.videoWidth > maxTex) { setFlat(true); return; }
      setReady(true);
    };
    const onTime = () => { setTime(video.currentTime); timeCb.current?.(video.currentTime); };

    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('ended', onPause);
    video.addEventListener('seeked', onFrame);
    video.addEventListener('loadeddata', onFrame);
    video.addEventListener('loadedmetadata', onMeta);
    video.addEventListener('timeupdate', onTime);
    const ro = new ResizeObserver(() => draw(false));
    ro.observe(canvas);

    return () => {
      disposed = true;
      cancelAnimationFrame(loop);
      ro.disconnect();
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('ended', onPause);
      video.removeEventListener('seeked', onFrame);
      video.removeEventListener('loadeddata', onFrame);
      video.removeEventListener('loadedmetadata', onMeta);
      video.removeEventListener('timeupdate', onTime);
      gl.deleteTexture(tex); gl.deleteBuffer(buf); gl.deleteProgram(program);
    };
  }, [src]);

  /* ----- looking around -------------------------------------------------- */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || flat) return;
    let dragging = false, lastX = 0, lastY = 0, pointer = -1;
    const pinch = new Map<number, { x: number; y: number }>();
    let pinchStart = 0, fovStart = cam.current.fov;
    const look = (dx: number, dy: number) => {
      const k = cam.current.fov / Math.max(1, canvas.clientHeight);
      cam.current.yaw -= dx * k;
      cam.current.pitch = Math.max(-MAX_PITCH, Math.min(MAX_PITCH, cam.current.pitch + dy * k));
      redraw.current();
    };
    const zoom = (f: number) => {
      cam.current.fov = Math.max(MIN_FOV, Math.min(MAX_FOV, f));
      redraw.current();
    };
    const down = (e: PointerEvent) => {
      pinch.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pinch.size === 2) {
        const [a, b] = [...pinch.values()];
        pinchStart = Math.hypot(a!.x - b!.x, a!.y - b!.y); fovStart = cam.current.fov; dragging = false;
        return;
      }
      dragging = true; pointer = e.pointerId; lastX = e.clientX; lastY = e.clientY;
      canvas.setPointerCapture(e.pointerId);
      setHinted(true);
    };
    const move = (e: PointerEvent) => {
      if (pinch.has(e.pointerId)) pinch.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pinch.size === 2 && pinchStart) {
        const [a, b] = [...pinch.values()];
        zoom(fovStart * pinchStart / Math.max(1, Math.hypot(a!.x - b!.x, a!.y - b!.y)));
        return;
      }
      if (!dragging || e.pointerId !== pointer) return;
      look(e.clientX - lastX, e.clientY - lastY);
      lastX = e.clientX; lastY = e.clientY;
    };
    const up = (e: PointerEvent) => {
      pinch.delete(e.pointerId);
      if (pinch.size < 2) pinchStart = 0;
      if (e.pointerId === pointer) { dragging = false; pointer = -1; }
    };
    const wheel = (e: WheelEvent) => { e.preventDefault(); zoom(cam.current.fov * (1 + e.deltaY * 0.0012)); };
    const key = (e: KeyboardEvent) => {
      const step = 40;
      if (e.key === 'ArrowLeft') look(step, 0);
      else if (e.key === 'ArrowRight') look(-step, 0);
      else if (e.key === 'ArrowUp') look(0, step);
      else if (e.key === 'ArrowDown') look(0, -step);
      else if (e.key === '+' || e.key === '=') zoom(cam.current.fov * 0.9);
      else if (e.key === '-') zoom(cam.current.fov * 1.1);
      else return;
      e.preventDefault(); setHinted(true);
    };
    canvas.addEventListener('pointerdown', down);
    canvas.addEventListener('pointermove', move);
    canvas.addEventListener('pointerup', up);
    canvas.addEventListener('pointercancel', up);
    canvas.addEventListener('wheel', wheel, { passive: false });
    canvas.addEventListener('keydown', key);
    return () => {
      canvas.removeEventListener('pointerdown', down);
      canvas.removeEventListener('pointermove', move);
      canvas.removeEventListener('pointerup', up);
      canvas.removeEventListener('pointercancel', up);
      canvas.removeEventListener('wheel', wheel);
      canvas.removeEventListener('keydown', key);
    };
  }, [flat]);

  /* ----- tilting the phone (opt-in) -------------------------------------- */
  useEffect(() => {
    if (!tilt) return;
    let base: number | null = null;
    const on = (e: DeviceOrientationEvent) => {
      if (e.alpha === null || e.beta === null) return;
      const yaw = (e.alpha * Math.PI) / 180;
      if (base === null) base = yaw + cam.current.yaw;
      cam.current.yaw = base - yaw;
      cam.current.pitch = Math.max(-MAX_PITCH, Math.min(MAX_PITCH, ((e.beta - 90) * Math.PI) / 180));
      redraw.current();
    };
    window.addEventListener('deviceorientation', on);
    return () => window.removeEventListener('deviceorientation', on);
  }, [tilt]);

  const toggleTilt = async () => {
    if (tilt) { setTilt(false); return; }
    const D = DeviceOrientationEvent as unknown as { requestPermission?: () => Promise<string> };
    if (typeof D.requestPermission === 'function') {
      try { if ((await D.requestPermission()) !== 'granted') return; } catch { return; }
    }
    setTilt(true); setHinted(true);
  };

  const play = () => { const v = videoRef.current; if (!v) return; if (v.paused) void v.play(); else v.pause(); };
  const seek = (s: number) => {
    const v = videoRef.current;
    if (v) { v.currentTime = s; setTime(s); timeCb.current?.(s); }
  };
  const fullscreen = () => {
    const el = wrapRef.current;
    if (!el) return;
    if (document.fullscreenElement) void document.exitFullscreen();
    else void el.requestFullscreen?.();
  };

  if (flat) {
    return (
      <div className="v360 v360-flat">
        <video src={src} controls playsInline preload="metadata" className="v360-flatvideo" />
        <p className="muted v360-note">This is a 360 video. This device cannot turn it into a view you can look
          around in, so it is shown flat: the whole room at once, stretched.</p>
      </div>
    );
  }

  return (
    <div className="v360" ref={wrapRef}>
      <div className="v360-stage">
        <canvas ref={canvasRef} className="v360-canvas" tabIndex={0}
          role="img" aria-label={`360 video of ${title}. Drag or use the arrow keys to look around.`} />
        {/* The source. Hidden: the canvas is what is seen. */}
        <video ref={videoRef} src={src} playsInline preload="metadata" crossOrigin="anonymous"
          className="v360-source" muted={muted} />
        {!playing && (
          <button type="button" className="v360-bigplay" onClick={play} aria-label="Play the 360 video">
            <span aria-hidden>▶</span>
          </button>
        )}
        {ready && !hinted && <div className="v360-hint" aria-hidden>Drag to look around</div>}
        {current && <div className="v360-room" aria-live="polite">{current.label}</div>}
        <div className="v360-badge" aria-hidden>360°</div>
      </div>

      <div className="v360-bar">
        <button type="button" className="v360-btn" onClick={play} aria-label={playing ? 'Pause' : 'Play'}>
          {playing ? '❚❚' : '▶'}
        </button>
        <span className="v360-time num">{clock(time)} / {clock(length)}</span>
        <div className="v360-track">
          <input type="range" min={0} max={length || 0} step={0.1} value={time}
            onChange={(e) => seek(Number(e.target.value))} aria-label="Position in the video" />
          {length > 0 && ordered.map((c) => (
            <span key={`${c.section}-${c.at}`} className="v360-tick" style={{ left: `${(100 * c.at) / length}%` }}
              title={c.label} aria-hidden />
          ))}
        </div>
        {canTilt && (
          <button type="button" className={`v360-btn${tilt ? ' on' : ''}`} onClick={toggleTilt}
            aria-pressed={tilt} aria-label="Look around by moving the phone">Tilt</button>
        )}
        <button type="button" className={`v360-btn${muted ? '' : ' on'}`} onClick={() => setMuted((m) => !m)}
          aria-pressed={!muted} aria-label={muted ? 'Turn the sound on' : 'Turn the sound off'}>
          {muted ? 'Sound off' : 'Sound on'}
        </button>
        <button type="button" className="v360-btn" onClick={fullscreen} aria-label="Full screen">⛶</button>
      </div>

      {ordered.length > 0 && (
        <nav className="v360-rooms" aria-label="Rooms in this video">
          {ordered.map((c) => (
            <button key={`${c.section}-${c.at}`} type="button"
              className={`v360-chip${current === c ? ' on' : ''}`}
              onClick={() => { seek(c.at); const v = videoRef.current; if (v?.paused) void v.play(); }}>
              <span>{c.label}</span><span className="num">{clock(c.at)}</span>
            </button>
          ))}
        </nav>
      )}
    </div>
  );
}
